// Public membership application form (Issue #8). Rendered inside the landing
// page "Join the Adventure" section. Posts to /api/applications with no auth.
//
// Consent checkbox is OPTIONAL here (it is NOT an SMS opt-in) and defaults
// UNCHECKED. SMS opt-in proper happens later at invite acceptance, once a
// member is linked and a phone is on file.

import { useState } from 'react';
import { submitApplication } from '../api';

const EMPTY = {
  fullName: '', email: '', phone: '', householdSize: '', city: '',
  heardFrom: '', interests: [], message: '', consented: false,
};

// Must match APPLICATION_INTERESTS in api/src/data/applications.js.
const INTEREST_OPTIONS = [
  { value: 'camping', label: 'Camping' },
  { value: 'fishing', label: 'Fishing' },
  { value: 'hunting', label: 'Hunting' },
  { value: 'hiking', label: 'Hiking' },
  { value: 'atv', label: 'ATV / Trail Riding' },
  { value: 'events', label: 'Family Events' },
];

export default function ApplicationForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error
  const [error, setError] = useState(null);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleInterest(value) {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(value)
        ? f.interests.filter((i) => i !== value)
        : [...f.interests, value],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!form.fullName.trim()) { setError('Please enter your name.'); return; }
    if (!form.email.trim()) { setError('Please enter your email.'); return; }

    setStatus('submitting');
    try {
      const res = await submitApplication({
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        householdSize: form.householdSize === '' ? undefined : Number(form.householdSize),
        city: form.city,
        heardFrom: form.heardFrom,
        interests: form.interests,
        message: form.message,
        consented: form.consented,
      });
      if (res.ok) {
        setStatus('done');
        setForm(EMPTY);
      } else {
        setStatus('error');
        setError((res.data && res.data.error) || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  }

  if (status === 'done') {
    return (
      <div className="lp-info-card app-form app-form-done">
        <h3>Application Received</h3>
        <p>Thanks for your interest in Eagle Ridge Farm Outdoor Club. We'll be in touch soon.</p>
      </div>
    );
  }

  return (
    <form className="lp-info-card app-form" onSubmit={handleSubmit}>
      <h3>Request a Tour or Membership Application</h3>
      <p className="lp-subtle">
        Tell us a little about you and we'll reach out with next steps.
      </p>

      <label className="app-field">
        <span>Name <em>*</em></span>
        <input
          type="text"
          value={form.fullName}
          onChange={(e) => update('fullName', e.target.value)}
          maxLength={200}
          required
          autoComplete="name"
        />
      </label>

      <label className="app-field">
        <span>Email <em>*</em></span>
        <input
          type="email"
          value={form.email}
          onChange={(e) => update('email', e.target.value)}
          maxLength={256}
          required
          autoComplete="email"
        />
      </label>

      <label className="app-field">
        <span>Phone <span className="lp-subtle">(optional)</span></span>
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => update('phone', e.target.value)}
          maxLength={40}
          autoComplete="tel"
          placeholder="(502) 555-1234"
        />
      </label>

      <div className="app-field-row">
        <label className="app-field">
          <span>Town / City <span className="lp-subtle">(optional)</span></span>
          <input
            type="text"
            value={form.city}
            onChange={(e) => update('city', e.target.value)}
            maxLength={120}
            autoComplete="address-level2"
            placeholder="Corinth, KY"
          />
        </label>

        <label className="app-field app-field-narrow">
          <span>Family size <span className="lp-subtle">(optional)</span></span>
          <input
            type="number"
            min={1}
            max={20}
            value={form.householdSize}
            onChange={(e) => update('householdSize', e.target.value)}
            placeholder="4"
          />
        </label>
      </div>

      <label className="app-field">
        <span>How did you hear about us? <span className="lp-subtle">(optional)</span></span>
        <input
          type="text"
          value={form.heardFrom}
          onChange={(e) => update('heardFrom', e.target.value)}
          maxLength={200}
          placeholder="A friend, Facebook, a sign…"
        />
      </label>

      <fieldset className="app-interests">
        <legend>What are you interested in? <span className="lp-subtle">(optional)</span></legend>
        <div className="app-interests-grid">
          {INTEREST_OPTIONS.map((opt) => (
            <label key={opt.value} className="app-interest">
              <input
                type="checkbox"
                checked={form.interests.includes(opt.value)}
                onChange={() => toggleInterest(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="app-field">
        <span>Anything you'd like us to know? <span className="lp-subtle">(optional)</span></span>
        <textarea
          value={form.message}
          onChange={(e) => update('message', e.target.value)}
          maxLength={2000}
          rows={4}
        />
      </label>

      <label className="app-consent">
        <input
          type="checkbox"
          checked={form.consented}
          onChange={(e) => update('consented', e.target.checked)}
        />
        <span>
          I agree to be contacted by Eagle Ridge Farm Outdoor Club about membership.
          See our <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
        </span>
      </label>

      {error && <p className="app-error" role="alert">{error}</p>}

      <button type="submit" className="primary" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending…' : 'Submit Application'}
      </button>
    </form>
  );
}
